/**
 * Compliance Dashboard Component
 *
 * Displays SLA compliance metrics, historical trends, and breach reports
 */

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { generateSLAReport, calculateHistoricalTrends } from '@/services/analyticsService'
import type { Task } from '@/types'

interface ComplianceDashboardProps {
  tasks: Task[]
  threshold?: number
  period?: 'day' | 'week' | 'month'
  isLoading?: boolean
}

export function ComplianceDashboard({
  tasks,
  threshold = 95,
  period = 'week',
  isLoading = false
}: ComplianceDashboardProps) {
  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    )
  }

  const report = generateSLAReport(tasks, threshold, period)
  const trends = calculateHistoricalTrends(tasks, period)

  return (
    <div className="space-y-6">
      {/* Overall SLA Status */}
      <Card>
        <CardHeader>
          <CardTitle>SLA Compliance Status</CardTitle>
          <CardDescription>
            Current compliance against {threshold}% success rate threshold
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className={`p-6 rounded-lg border-2 ${
            report.overall.slaMet
              ? 'bg-green-50 border-green-500 dark:bg-green-950/20'
              : 'bg-red-50 border-red-500 dark:bg-red-950/20'
          }`}>
            <div className="flex items-center justify-between mb-2">
              <p className="text-lg font-semibold">
                {report.overall.slaMet ? '✓ SLA MET' : '✗ SLA BREACHED'}
              </p>
              <p className="text-4xl font-bold">
                {report.overall.successRate.toFixed(1)}%
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
              <div>
                <p className="text-muted-foreground">Threshold</p>
                <p className="font-medium">{threshold}%</p>
              </div>
              <div>
                <p className="text-muted-foreground">Gap</p>
                <p className={`font-medium ${report.overall.gap >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {report.overall.gap > 0 ? '+' : ''}{report.overall.gap.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Compliance Rate</p>
                <p className="font-medium">
                  {report.overall.complianceRate.toFixed(1)}%
                </p>
              </div>
              <div>
                <p className="text-muted-foreground">Total Tasks</p>
                <p className="font-medium">
                  {report.overall.totalTasks}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Historical Trends */}
      <Card>
        <CardHeader>
          <CardTitle>Historical Trends</CardTitle>
          <CardDescription>Success rate trends over time</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-card rounded-lg border">
              <p className="text-sm text-muted-foreground">Success Rate</p>
              <p className="text-3xl font-bold">{trends.overall.successRate.toFixed(1)}%</p>
            </div>
            <div className="p-4 bg-card rounded-lg border">
              <p className="text-sm text-muted-foreground">Total Tasks</p>
              <p className="text-3xl font-bold">{trends.overall.totalTasks}</p>
            </div>
            <div className="p-4 bg-card rounded-lg border">
              <p className="text-sm text-muted-foreground">Trend</p>
              <p className="text-2xl font-bold capitalize">
                {trends.trends.direction}
                {trends.trends.direction !== 'stable' && (
                  <span className="text-sm ml-2">
                    ({trends.trends.change > 0 ? '+' : ''}{trends.trends.change.toFixed(1)}%)
                  </span>
                )}
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <h3 className="text-sm font-semibold mb-2">Recent Periods</h3>
            {trends.periods.slice(-5).map((periodData: any) => (
              <div
                key={periodData.timestamp}
                className="flex justify-between items-center p-3 bg-muted/50 rounded"
              >
                <span className="text-sm">
                  {new Date(periodData.date).toLocaleDateString()}
                </span>
                <div className="flex gap-4 items-center">
                  <span className={`text-sm font-medium ${
                    periodData.successRate >= threshold ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {periodData.successRate.toFixed(1)}%
                  </span>
                  <span className="text-sm text-muted-foreground">
                    {periodData.total} tasks
                  </span>
                  <Badge variant={periodData.successRate >= threshold ? 'default' : 'destructive'}>
                    {periodData.successRate >= threshold ? 'Pass' : 'Fail'}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* SLA Breaches */}
      {report.breaches.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>SLA Breaches ({report.breaches.length})</CardTitle>
            <CardDescription>Periods where SLA was not met</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {report.breaches.map((breach: any, i: any) => (
                <div
                  key={i}
                  className="p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200 dark:border-red-900"
                >
                  <div className="flex justify-between items-start mb-2">
                    <span className="font-medium">
                      {new Date(breach.date).toLocaleDateString()}
                    </span>
                    <Badge variant="destructive">
                      {breach.successRate.toFixed(1)}% ({breach.gap.toFixed(1)}% below)
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {breach.failedCount} failures out of {breach.totalCount} tasks
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* No Breaches Message */}
      {report.breaches.length === 0 && report.overall.slaMet && (
        <Card>
          <CardContent className="pt-6">
            <div className="text-center p-6 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200 dark:border-green-900">
              <p className="text-lg font-semibold text-green-700 dark:text-green-400">
                ✓ No SLA Breaches
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                All periods are meeting or exceeding the SLA threshold
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
